import App from "next/app";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { QueryClient, QueryClientProvider, HydrationBoundary, QueryCache } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { parseCookies, setCookie } from "nookies";
import { CookieKeys } from "../types/cookie-keys";
import { StyleSheetManager } from "styled-components";
import { GlobalStyle } from "../theme/global-styles";
import dynamic from "next/dynamic";
import "react-datepicker/dist/react-datepicker.css";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import "quill/dist/quill.bubble.css";
import "quill/dist/quill.core.css";
import "simplebar-react/dist/simplebar.min.css";
import { ErrorBoundary } from "../components/ErrorBoundary";
import LocalStore, { LocalStorageKeys } from "../localstorage/LocalStorage";
import { CookieContext } from "../context/cookieContext";
import { Toaster } from "react-hot-toast";
import { AuthoryToastConfig } from "../shared-logic/AuthoryToastConfig";
import { Generic403, Generic404, Generic500 } from "../pagelayouts/generic-error-page";
import { XRequestHostHeader, XRequestHostSlug } from "../types/headers";
import { DomainContext, HostAuthorContext, HostContext } from "../context/domainContext";
import { AddElementSkeletonTracker } from "../context/addElementSkeletonContext";
import { logAxiosError } from "../logging/logSentryError";
import { AxiosError } from "axios";
import { Errors } from "../types/api";
import { NotificationHandler, V3GenericErrorHandler } from "../api/error-handler";
import isPropValid from "@emotion/is-prop-valid";
import { computeAcquisitionChannel } from "@authory-packages/core";
import { AuthoryThemeProvider } from "../theme/ThemeProvider";
import { CollectionSortingContext } from "../context/collectionsDrag";
import { ClientOnly } from "../components/ClientOnly";
import { PublicProfileAccessKey } from "../context/publicProfileAccessKeyContext";
import { PublicCollectionAccessKey } from "../context/publicCollectionAccessKeyContext";
import { PublicContentItemAccessKey } from "../context/publicContentItemAccessKeyContext";
import { AuthenticatedUserThemeSetter } from "../components/AuthenticatedUserTheme";

const TrackPageViewManager = dynamic(() => import("../components/TrackPageViewManager").then((m) => m.TrackPageViewManager), { ssr: false });

// This implements the default behavior from styled-components v5 - we can only remove this once we move all component to use transient props
function shouldForwardProp(propName: string, target: any) {
    if (typeof target === "string") {
        // For HTML elements, forward the prop if it is a valid HTML attribute
        return isPropValid(propName);
    }
    // For other elements, forward all props
    return true;
}

const isProduction = process.env.NODE_ENV === "production";

export default function MyApp({ Component, pageProps }: any) {
    const router = useRouter();
    const { author, statusCode } = pageProps;
    const [domain] = useState(pageProps.domain);
    const cookies = parseCookies();

    const isAppRoute = router.route.startsWith("/app");
    const AuthenticatedUserThemeSetterToUse = !isAppRoute ? React.Fragment : AuthenticatedUserThemeSetter;

    const [queryClient] = React.useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        retry: false,
                        throwOnError: true,
                        staleTime: Infinity,
                        gcTime: 60 * 1000
                    }
                },
                queryCache: new QueryCache({
                    onError: (error, query) => {
                        if (query?.meta?.logNotFound && (error as AxiosError)?.response?.status !== Errors.NotFound) {
                            logAxiosError(error as AxiosError);
                        }

                        if (query?.meta?.useGenericErrorHandler && query?.meta?.toastErrorCallback) {
                            V3GenericErrorHandler(query?.meta?.toastErrorCallback as NotificationHandler)(error as AxiosError);
                        }
                    }
                })
            })
    );

    useEffect(() => {
        if (router.pathname.endsWith("/embed")) return;

        if ("serviceWorker" in navigator) {
            try {
                navigator.serviceWorker
                    .register("/authory_static_files/remove_assets_cookies.js")
                    .then((registration) => {
                        registration.update();
                        console.info("Service Worker registration successful with scope: ", registration.scope);
                    })
                    .catch((err) => console.info("Service Worker registration failed:", err));
            } catch {}
        }
    }, []);

    useEffect(() => {
        // Wait for the router to hydrate the query string. On statically generated pages
        // router.query only contains the dynamic route params until isReady is true, so the
        // UTM params would otherwise be missing on the first run.
        if (!router.isReady) return;

        // Get UTM props from query
        let query = router.query;

        if (query.fbclid && typeof query.fbclid === "string") {
            new LocalStore(LocalStorageKeys.tracking_fbclid).set(query.fbclid);
        }

        if (query.utm_source && typeof query.utm_source === "string") {
            new LocalStore(LocalStorageKeys.tracking_utm_source).set(query.utm_source);
        }

        if (query.utm_medium && typeof query.utm_medium === "string") {
            new LocalStore(LocalStorageKeys.tracking_utm_medium).set(query.utm_medium);
        }

        if (query.utm_campaign && typeof query.utm_campaign === "string") {
            new LocalStore(LocalStorageKeys.tracking_utm_campaign).set(query.utm_campaign);
        }

        if (query.utm_term && typeof query.utm_term === "string") {
            new LocalStore(LocalStorageKeys.tracking_utm_term).set(query.utm_term);
        }

        if (query.utm_content && typeof query.utm_content === "string") {
            new LocalStore(LocalStorageKeys.tracking_utm_content).set(query.utm_content);
        }

        if (document.referrer) {
            let domain;

            try {
                domain = new URL(document.referrer).hostname;
            } catch {
                domain = "";
            }

            // Save document referrer.
            const reservedHostnames = ["authory.com", "www.authory.com", "localhost", "staging.authory.com", null];

            if (!reservedHostnames.includes(domain)) {
                new LocalStore(LocalStorageKeys.tracking_referrer).set(document.referrer);
            }
        }

        const cookieOptions = { maxAge: 30 * 24 * 60 * 60, path: "/", sameSite: "lax" } as const;
        const landingPage = window.location.pathname;

        if (!cookies[CookieKeys.landing_page]) {
            setCookie(null, CookieKeys.landing_page, landingPage, cookieOptions);
        }

        if (!cookies[CookieKeys.first_touch_channel]) {
            const utm = {
                source: typeof query.utm_source === "string" ? query.utm_source : undefined,
                medium: typeof query.utm_medium === "string" ? query.utm_medium : undefined,
                referrer: document.referrer || undefined
            };
            setCookie(null, CookieKeys.first_touch_channel, computeAcquisitionChannel(utm, landingPage), cookieOptions);
        }
    }, [router.isReady, router.query]);

    return (
        <StyleSheetManager shouldForwardProp={shouldForwardProp}>
            <GlobalStyle />
            <AuthoryThemeProvider
                initialThemeProps={
                    author
                        ? {
                              customization: {
                                  profileFontSet: author.profileFontSet,
                                  backgroundColor: author.backgroundColor,
                                  accentcolour: author.accentcolour,
                                  profileCollectionLayout: author.profileCollectionLayout,
                                  defaultView: author.defaultView,
                                  profileStyle: author.profileStyle
                              }
                          }
                        : {}
                }
            >
                <ErrorBoundary>
                    <CookieContext.Provider value={cookies["token"]}>
                        <DomainContext.Provider value={domain}>
                            <CollectionSortingContext>
                                <PublicProfileAccessKey initialAccessKey={pageProps.profileAccessKey}>
                                    <PublicCollectionAccessKey initialResolveKey={pageProps.collectionAccessKey}>
                                        <PublicContentItemAccessKey initialResolveKey={pageProps.contentItemAccessKey}>
                                            <AddElementSkeletonTracker>
                                                <HostContext.Provider value={pageProps.host}>
                                                    <HostAuthorContext.Provider value={pageProps.hostAuthor}>
                                                        <QueryClientProvider client={queryClient}>
                                                            <HydrationBoundary state={pageProps.dehydratedState}>
                                                                <AuthenticatedUserThemeSetterToUse>
                                                                    <Toaster
                                                                        position="bottom-center"
                                                                        toastOptions={AuthoryToastConfig}
                                                                        containerClassName={`ToasterAuthoryContainer`}
                                                                    />
                                                                    <TrackPageViewManager />
                                                                    {statusCode === 404 ? <Generic404 /> : null}
                                                                    {statusCode === 500 ? <Generic500 /> : null}
                                                                    {statusCode === 403 ? (
                                                                        <Generic403 target={pageProps?.unavailableResourceType} />
                                                                    ) : null}
                                                                    {statusCode === 200 || !statusCode ? <Component {...pageProps} /> : null}
                                                                    <ClientOnly>
                                                                        {!isProduction && <ReactQueryDevtools initialIsOpen={false} />}
                                                                    </ClientOnly>
                                                                </AuthenticatedUserThemeSetterToUse>
                                                            </HydrationBoundary>
                                                        </QueryClientProvider>
                                                    </HostAuthorContext.Provider>
                                                </HostContext.Provider>
                                            </AddElementSkeletonTracker>
                                        </PublicContentItemAccessKey>
                                    </PublicCollectionAccessKey>
                                </PublicProfileAccessKey>
                            </CollectionSortingContext>
                        </DomainContext.Provider>
                    </CookieContext.Provider>
                </ErrorBoundary>
            </AuthoryThemeProvider>
        </StyleSheetManager>
    );
}

MyApp.getInitialProps = async (appContext: any) => {
    const appProps = await App.getInitialProps(appContext);

    return {
        pageProps: {
            ...appProps.pageProps,
            domain: appContext.ctx.res?.getHeader(XRequestHostHeader),
            hostAuthor: appContext.ctx.res?.getHeader(XRequestHostSlug),
            host: appContext.ctx.req ? appContext.ctx.req?.headers["host"] : window?.location?.host
        }
    };
};
